import {
    Component,
    ViewChild,
    ViewEncapsulation,
    OnInit,
    AfterViewInit,
    OnDestroy,
    HostBinding
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    FormGroup,
    FormControl,
    Validators
} from '@angular/forms';

import {
    tap,
    map,
    skip,
    delay,
    filter,
    takeUntil,
    distinctUntilChanged,
    switchMap,
    concatMap
} from 'rxjs/operators';

import {
    Subject,
    Observable,
    BehaviorSubject,
    of,
    fromEvent,
    merge,
    throwError as _throw
} from 'rxjs';

import * as Screeps from '@screeps/map';

import {
    ApiService,
    RouteSegmentService
} from '../../core/upgraded-providers';

import { MapComponent } from '../map.component';
import { TooltipComponent } from '../tooltip.component';
import { WorldMapService } from '../world-map.service';
import { UnitsService } from '../units.service';
import { VisualService } from '../visual.service';
import { ShowDlgWorldOopsService } from '../show-dlg-world-oops.service';
import { ShowDlgWorldWelcomeService } from '../show-dlg-world-welcome.service';
import { ShowDlgWorldTipsService } from '../show-dlg-world-tips.service';
import { ShowDlgWorldPreferencesService } from '../show-dlg-preferences.service';

import {
    DISPLAY_NONE,
    DISPLAY_OWNER,
    DISPLAY_MINERALS,

    default as DISPLAY_OPTIONS
} from './display-options';
import INTERVAL_OPTIONS from './interval-options';

const LEFT_MOUSE_BUTTON = 0;
const WHEEL_MOUSE_BUTTON = 1;
const RIGHT_MOUSE_BUTTON = 2;

const WORLD_STATUS_LOST = 'lost';
const WORLD_STATUS_EMPTY = 'empty';
const WORLD_DEFAULT_SHARD = 'shard0';

const ANIM_SNAKE_DURATION = 500;

const statsCache = {};
@Component({
    selector: 'app-world-map-base',
    templateUrl: './base.component.pug',
    styleUrls: ['./base.component.less'],
    providers: [WorldMapService, UnitsService, VisualService],
    encapsulation: ViewEncapsulation.None
})
export class BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    MIN_SCALE = Screeps.MIN_SCALE;
    MAX_SCALE = Screeps.MAX_SCALE;
    SCALE_DELTA = Screeps.SCALE_DELTA;

    public settingsForm = new FormGroup({
        shard: new FormControl(null, Validators.required),
        display: new FormControl(DISPLAY_OPTIONS[1].value, Validators.required),
        interval: new FormControl(INTERVAL_OPTIONS[0].value, Validators.required)
    });

    size: {
        width: number;
        height: number;
    } = null;

    scale: number = 3;
    _scaleSbj: BehaviorSubject<any> = new BehaviorSubject(2);
    scale$: Observable<any> = this._scaleSbj.asObservable()
        .pipe(map((scale) => parseFloat(screepsScaleToPercent(scale).toFixed(2))));

    _centerSbj: BehaviorSubject<string> = new BehaviorSubject('');
    center$: Observable<string> = this._centerSbj.asObservable();

    // _claimSbj: BehaviorSubject<boolean> = new BehaviorSubject(false);
    _blendSbj: BehaviorSubject<boolean> = new BehaviorSubject(false);
    _unitsSbj: BehaviorSubject<boolean> = new BehaviorSubject(false);
    units$: Observable<boolean> = this._unitsSbj.asObservable();
    _unitsRestrictedSbj: BehaviorSubject<boolean> = new BehaviorSubject(true);
    unitsRestricted$: Observable<boolean> = this._unitsRestrictedSbj.asObservable();

    DISPLAY_OPTIONS = DISPLAY_OPTIONS;
    _displaySbj: BehaviorSubject<any> = new BehaviorSubject(DISPLAY_OPTIONS[1].value);
    display$: Observable<any> = this._displaySbj.asObservable()
        .pipe(map((value) => this.DISPLAY_OPTIONS.find((_) => _.value === value)));

    INTERVAL_OPTIONS = INTERVAL_OPTIONS;
    _intervalSbj: BehaviorSubject<any> = new BehaviorSubject(INTERVAL_OPTIONS[0]);
    interval$: Observable<any> = this._intervalSbj.asObservable();

    shards = [];
    cacheUsers = {};

    @ViewChild('map', {static: false}) mapRef: MapComponent;
    @ViewChild('tooltip', {static: false}) tooltipRef: TooltipComponent;

    @HostBinding('class.--novice-area-panel') isNoviceAreaPanel: boolean = false;

    _destroySbj: Subject<void> = new Subject();
    _destroyBoundHandlersSbj: Subject<void> = new Subject();

    _roomsSbj: BehaviorSubject<string[]> = new BehaviorSubject([]);
    rooms$: Observable<any[]> = this._roomsSbj.asObservable();

    _boundSbj: BehaviorSubject<any> = new BehaviorSubject(null);
    bound$: Observable<any[]> = this._boundSbj.asObservable();

    _drawMapRoomsSbj: Subject<IRoom[]> = new Subject();
    _drawMapStatsSbj: Subject<string[]> = new Subject();
    _drawMapUsersSbj: Subject<string[]> = new Subject();
    _drawMapMineralsSbj: Subject<string[]> = new Subject();
    _drawMapDecorationsSbj: Subject<string[]> = new Subject();

    _getMapStatsSbj: Subject<any> = new Subject();
    _getRoomsTerrainSbj: Subject<string[]> = new Subject();

    _updateStatsSbj: Subject<string[]> = new Subject();
    _noviceAreaSbj: Subject<void> = new Subject();
    _showWelcomeSbj: Subject<void> = new Subject();
    _stopDrawUtitsSbj: Subject<void> = new Subject();

    _searchFailSbj: Subject<boolean> = new Subject();
    searchFail$: Observable<boolean> = this._searchFailSbj.asObservable()
        .pipe(tap((value) => value && setTimeout(() => this._searchFailSbj.next(false), ANIM_SNAKE_DURATION)));

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _unitsService: UnitsService,
        private _visualService: VisualService,
        private _worldMapService: WorldMapService,
        private _apiService: ApiService,
        private _routeSegmentService: RouteSegmentService,
        private _dlgWorldRespawnService: ShowDlgWorldOopsService,
        private _dlgWorldWelcomeService: ShowDlgWorldWelcomeService,
        private _dlgWorldTipsService: ShowDlgWorldTipsService,
        private _dlgWorldPreferencesSrv: ShowDlgWorldPreferencesService
    ) {
    }

    ngOnInit() {
        if (this._apiService.options.serverData.shards.length > 1) {
            this.shards = this._apiService.options.serverData.shards.map((name) => ({
                name, value: name
            }));
        }
    }

    ngOnDestroy() {
        this._destroySbj.next();
        this._destroySbj.complete();

        this._destroyBoundHandlersSbj.next();
        this._destroyBoundHandlersSbj.complete();

        this._worldMapService.destroy();
    }

    ngAfterViewInit() {
        this.initDrawUnits();
        this.initDrawMapVisual();

        merge(
            this._route.data.pipe(filter(({ status }) => status === WORLD_STATUS_EMPTY)),
            this._showWelcomeSbj.asObservable()
        )
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap(() => this.isNoviceAreaPanel = true))
            .pipe(switchMap(() => this._dlgWorldWelcomeService.show()))
            .pipe(switchMap(() => this._dlgWorldTipsService.show()))
            .pipe(tap(() => this._showWelcomeSbj.next()))
            .subscribe(() => {}, () => {
                // Noop handler's error
            });

        this._route.data
            .pipe(takeUntil(this._destroySbj))
            .pipe(filter(({ status }) => status === WORLD_STATUS_LOST))
            .pipe(switchMap(() => this._dlgWorldRespawnService.show()))
            // @ts-ignore
            .pipe(switchMap(() => this.userRespawn()))
            .pipe(tap(() => this._showWelcomeSbj.next()))
            .subscribe(() => {}, () => {
                // Noop handler's error
            });

        this._route.data
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap(({ size }) => this.mapRef.setSize(size)))
            .subscribe();

        let i = 0;
        this._noviceAreaSbj.asObservable()
            .pipe(takeUntil(this._destroySbj))
            .pipe(map(() => this._route.snapshot.data.worldStartRoom[i++]))
            .pipe(tap(() => i = i === this._route.snapshot.data.worldStartRoom.length ? 0 : i))
            .pipe(map((path: string) => path.match(/^(.*)\/(.*)$/)))
            .pipe(map(([, shard, room]) => ([shard, room])))
            .pipe(map(([shard, room]) => ([shard, roomToPos(room).join(',')])))
            .pipe(tap(([shard, pos]) => this._updateRouteData([shard], { pos })))
            .subscribe();

        this._route.params
            .pipe(takeUntil(this._destroySbj))
            .pipe(map(({ shard }) => shard))
            .pipe(distinctUntilChanged())
            .pipe(tap(() => this.initBoundHandlers()))
            .pipe(filter((shard) => shard !== this.settingsForm.value.shard))
            .pipe(tap((shard) => this.settingsForm.patchValue({ shard })))
            .subscribe();


        this._route.queryParams
            .pipe(takeUntil(this._destroySbj))
            .pipe(map(({ display }) => display || DISPLAY_OPTIONS[1].value))
            .pipe(distinctUntilChanged())
            .pipe(tap((display) => this._displaySbj.next(display)))
            .pipe(tap((display) => this.onChangeDisplayType(display)))
            .pipe(tap(() => this._updateStatsSbj.next()))
            .pipe(filter((display) => display !== this.settingsForm.value.display))
            .pipe(tap((display) => this.settingsForm.patchValue({ display })))
            .subscribe();

        this._route.queryParams
            .pipe(takeUntil(this._destroySbj))
            .pipe(map(({ scale }) => parseFloat(scale || 50)))
            .pipe(distinctUntilChanged())
            .pipe(map((scale) => percentToScreepsScale(scale)))
            .pipe(tap((scale) => this._scaleSbj.next(scale)))
            .pipe(tap((scale) => this.mapRef.setScale(scale)))
            .subscribe();

        this.scale$
            .pipe(filter((scale) => scale < 50))
            .pipe(tap(() => this._unitsRestrictedSbj.next(true)))
            .subscribe();

        this.scale$
            .pipe(filter((scale) => scale >= 50))
            .pipe(tap(() => this._unitsRestrictedSbj.next(false)))
            .subscribe();

        this._route.queryParams
            .pipe(takeUntil(this._destroySbj))
            .pipe(map(({ pos }) => pos ? pos.split(',').map((_) => parseFloat(_)) : [0, 0]))
            .pipe(distinctUntilChanged())
            .pipe(tap((center) => this.mapRef.setCenter(center)))
            .subscribe();

        this._route.queryParams
            .pipe(takeUntil(this._destroySbj))
            .pipe(map(({ units }) => units === 'false' ? false : true))
            .pipe(distinctUntilChanged())
            .pipe(tap((units) => this._unitsSbj.next(units)))
            .subscribe();

        this._route.queryParams
            .pipe(takeUntil(this._destroySbj))
            .pipe(map(({ claim }) => claim === 'false' ? false : true))
            .pipe(distinctUntilChanged())
            .pipe(tap((claim) => this.mapRef.toggleStats(claim)))
            .subscribe();

        this._route.queryParams
            .pipe(takeUntil(this._destroySbj))
            .pipe(map(({ visual }) => visual === 'false' ? false : true))
            .pipe(distinctUntilChanged())
            .pipe(tap((visual) => this.mapRef.toggleLayer(Screeps.VISUAL_LAYER, visual)))
            .subscribe();

        // this._route.queryParams
        //     .pipe(takeUntil(this._destroySbj))
        //     .pipe(map(({ decor }) => decor === 'false' ? false : true))
        //     .pipe(distinctUntilChanged())
        //     .pipe(tap((decor) => this.mapRef.toggleLayer(Screeps.DECORATIONS_LAYER, decor)))
        //     .subscribe();

        this._getRoomsTerrainSbj.asObservable()
            .pipe(takeUntil(this._destroySbj))
            .pipe(switchMap((rooms) => this._worldMapService.getRoomsTerrains(rooms, this.settingsForm.value.shard)))
            .pipe(concatMap((match: IRoom[]) => of(match).pipe(delay(10))))
            .pipe(filter((rooms: IRoom[]) => !!rooms.length))
            .pipe(tap((rooms: IRoom[]) => this._drawMapRoomsSbj.next(rooms)))
            .subscribe();

        this._getMapStatsSbj.asObservable()
            .pipe(takeUntil(this._destroySbj))
            .pipe(delay(100))
            .pipe(concatMap((rooms) => this.getMapStats(rooms)))
            .pipe(tap(({ stats }) => this._drawMapStatsSbj.next(stats)))
            .pipe(tap(({ users }) => this._drawMapUsersSbj.next(users)))
            .pipe(tap(({ stats }) => this._drawMapMineralsSbj.next(stats)))
            .pipe(tap(({ decorations }) => this._drawMapDecorationsSbj.next(decorations)))
            .subscribe();

        this._drawMapRoomsSbj.asObservable()
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap((rooms) => this.mapRef.drawRooms(rooms)))
            .subscribe();

        this._drawMapStatsSbj.asObservable()
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap((data) => this.mapRef.drawStats(data)))
            .subscribe();

        this._drawMapUsersSbj.asObservable()
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap((data) => this.mapRef.drawUsers(data)))
            .subscribe();

        this._drawMapMineralsSbj.asObservable()
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap((data) => this.mapRef.drawMinerals(data)))
            .subscribe();

        this._drawMapDecorationsSbj.asObservable()
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap((data) => this.mapRef.drawDecorations(data)))
            .subscribe();

        this._updateStatsSbj.asObservable()
            .pipe(takeUntil(this._destroySbj))
            // .pipe(switchMap(() => timer(0, 5000)))
            // .pipe(tap(() => {
            //     console.log(1.1);
            // }))
            .pipe(map(() => this._roomsSbj.getValue()))
            .pipe(tap((rooms) => this._getMapStatsSbj.next(rooms)))
            .subscribe();

        const scale$ = this.scale$
            .pipe(skip(1))
            .pipe(map((scale) => ({ scale })));

        const center$ = this.center$
            .pipe(skip(1))
            .pipe(map((pos) => ({ pos })));

        const display$ =  this.settingsForm.controls.display.valueChanges
            .pipe(map((display) => ({ display })));

        this.settingsForm.controls.shard.valueChanges
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap((shard) => this._updateRouteData([shard], {})))
            .subscribe();

        merge(scale$, center$, display$)
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap((queryParams) => this._updateRouteData([this.settingsForm.controls.shard.value], queryParams)))
            .subscribe();

        fromEvent(this.mapRef.nativeElement, 'mouseover')
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap(() => this.tooltipRef.show()))
            .pipe(map<MouseEvent, [number, number]>(({ offsetX, offsetY }) => [offsetX, offsetY]))
            .pipe(tap((pos) => this.tooltipRef.setPosition(pos)))
            .subscribe();

        fromEvent(this.mapRef.nativeElement, 'mouseout')
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap(() => this.tooltipRef.hide()))
            .subscribe();

        fromEvent(this.mapRef.nativeElement, 'mousemove')
            .pipe(takeUntil(this._destroySbj))
            .pipe(map<MouseEvent, [number, number]>(({ offsetX, offsetY }) => [offsetX, offsetY]))
            .pipe(tap((pos) => this.tooltipRef.setPosition(pos)))
            .subscribe();

        fromEvent(window, 'resize')
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap(() => this.mapRef.resize()))
            .subscribe();
    }

    initDrawUnits() {
        merge(
            this.units$
                .pipe(distinctUntilChanged())
                .pipe(filter((units) => !units)),
            this.unitsRestricted$
                .pipe(distinctUntilChanged())
                .pipe(filter((restricted) => restricted))
        )
            .pipe(takeUntil(this._destroySbj))
            .pipe(skip(2))
            .pipe(tap(() => this.hideUnits()))
            .subscribe();

        this.settingsForm.controls.shard.valueChanges
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap((shard) => this._unitsService.setShard(shard)))
            .pipe(switchMap(() => this.units$))
            .pipe(filter((units) => units))
            .pipe(switchMap(() => this.unitsRestricted$.pipe(distinctUntilChanged())))
            .pipe(filter((restricted) => !restricted))
            .pipe(tap(() => this.hideUnits()))
            .pipe(tap(() => this.showUnits()))
            .subscribe();
    }

    initBoundHandlers() {
        this._destroyBoundHandlersSbj.next();

        this.mapRef.bound$
            .pipe(takeUntil(this._destroyBoundHandlersSbj))
            .pipe(tap((bound) => this.onBound(bound)))
            .subscribe();

        this.mapRef.bound$
            .pipe(takeUntil(this._destroyBoundHandlersSbj))
            .pipe(map((bound) => Screeps.getRoomsInBound(bound)))
            .pipe(tap((rooms) => this._roomsSbj.next(rooms)))
            .pipe(tap(() => this._updateStatsSbj.next()))
            .subscribe();
    }

    initDrawMapVisual() {
        this.settingsForm.controls.shard.valueChanges
            .pipe(takeUntil(this._destroySbj))
            .pipe(switchMap((shard) => this._visualService.connect(shard)))
            .pipe(map((data) => data || []))
            .pipe(tap((data) => {
                console.debug('app-world-map-base::visualData', data);
                this.mapRef.getLayer(Screeps.VISUAL_LAYER).clear();
                this.mapRef.getLayer(Screeps.VISUAL_LAYER).draw(data);
                this.mapRef.render();
            }))
            .subscribe();
    }

    // tslint:disable-next-line: member-ordering
    isUnitsVisible: boolean = false;
    hideUnits() {
        if (!this.isUnitsVisible) {
            return;
        }

        this.isUnitsVisible = false;
        this._stopDrawUtitsSbj.next();

        this.mapRef.toggleUnits(false);
    }

    showUnits() {
        if (this.isUnitsVisible) {
            return;
        }

        this.isUnitsVisible = true;
        this.mapRef.bound$
            .pipe(takeUntil(this._destroySbj))
            .pipe(takeUntil(this._stopDrawUtitsSbj))
            .pipe(this._unitsService.asObservable())
            .pipe(map(([room, objects]) => ([room, {
                objects,
                blend: true,
                colors: Object.keys(objects).filter((key) => key.length === 24).reduce((colors, key) => {
                    const user = this.cacheUsers[key];

                    try {
                        const color = user.playerColor || user.badge.color1;

                        const r = parseInt(color.substr(1, 2), 16);
                        const g = parseInt(color.substr(3, 2), 16);
                        const b = parseInt(color.substr(5, 2), 16);

                        colors[user._id] = new Uint8ClampedArray([r, g, b]);
                    } catch (err) {
                        // Noop.
                    }

                    return colors;
                }, {})
            }])))
            .pipe(tap(([room, data]) => this.mapRef.drawUnits(room, data)))
            .subscribe();

        this.mapRef.toggleUnits(true);
    }

    onBound(bound: any) {
        const { x, y, width, height } = bound;

        const rw = Math.round(width / 2);
        const rh = Math.round(height / 2);
        const cx = rw + x;
        const cy = rh + y;
        const r = Math.round(Math.sqrt(rw * rw + rh * rh));

        const __cache__ = {};
        const getRoomsInCircle = (cx, cy, r) => circleBresenhams(cx, cy, r)
            .filter((point) => Screeps.checkPointInBound(point, bound))
            .reduce((rooms, point) => {
                const room = Screeps.getRoomNameByPoint(point);
                if (this.mapRef.contain(room)) {
                    return rooms;
                }

                if (__cache__[room]) {
                    return rooms;
                }

                rooms.push(room);
                __cache__[room] = room;

                return rooms;
            }, []);

        for (let i = 1; i <= r; i++) {
            const rooms = getRoomsInCircle(cx, cy, i);

            if (rooms.length) {
                this._getRoomsTerrainSbj.next(rooms);
            }
        }
    }

    async getMapStats(rooms: string[]): Promise<{ stats, users, decorations }> {
        const shard = this.settingsForm.value.shard;

        let data;
        try {
            const display = this._displaySbj.getValue();
            data = await this._worldMapService.getStats(rooms, shard, display);
        } catch (err) {
            setTimeout(() => this._getMapStatsSbj.next(rooms), 1000);

            return {
                stats: [],
                users: [],
                decorations: []
            };
        }

        const stats = [];
        const users = [];
        const roomsDecorations = {};

        Object.entries(data.users)
            .forEach(([key, user]) => this.cacheUsers[key] = user);

        Object.entries(data.stats)
            .filter(function ([, stat]: [string, any]) {
                try {
                    stat.own.username = data.users[stat.own.user].username;
                } catch (err) {
                }

                try {
                    stat.sign.username = data.users[stat.sign.user].username;
                } catch (err) {
                }

                try {
                    stat.hardSign.username = data.users[stat.hardSign.user].username;
                } catch (err) {
                }

                return true;
            })
            .reduce(([stats, users, roomsDecorations]: [any[], any[], any], [room, stat]) => {
                stats.push({ room, stat });

                // @ts-ignore
                const { own } = stat;
                if (own && data.users[own.user]) {
                    const user = data.users[own.user];

                    users.push({
                        room,
                        user: {
                            username: user.username,
                            level: own.level,
                            badgeUrl: `${ this._apiService.options.apiUrl }user/badge-svg?username=${ user.username }&border=1`
                        }
                    });
                }

                // @ts-ignore
                if (stat.decorations) {

                    // @ts-ignore
                    stat.decorations.forEach(function({ _id, activatedAt, decoration, active }) {
                        const { type, graphics, tiling, foregroundUrl, floorForegroundUrl } = data.decorations[decoration];

                        roomsDecorations[room] = roomsDecorations[room] || {
                            room,
                            decorations: []
                        };

                        roomsDecorations[room].decorations.push({
                            ...active,
                            tiling,
                            decoration: {
                                _id,
                                activatedAt,
                                type,
                                graphics,
                                foregroundUrl,
                                floorForegroundUrl
                            }
                        });
                    });
                }

                statsCache[room] = stat;

                return [stats, users, roomsDecorations];
            }, [stats, users, roomsDecorations]);

        return {
            stats,
            users,
            decorations: Object.values(roomsDecorations)
        };
    }

    onChangeScale(scale) {
        scale = Math.round(scale * 100) / 100;
        if (scale === this._scaleSbj.getValue()) {
            return;
        }

        this._scaleSbj.next(scale);
    }

    onChangeScalePercent(scale) {
        if (scale > 100) {
            scale = 100;
        }

        if (scale < 0) {
            scale = 0;
        }

        scale = percentToScreepsScale(scale);
        this._scaleSbj.next(scale);
    }

    onChangeCenter(center: [number, number]) {
        this._centerSbj.next(center.join(','));
        // this._updateRouteData([this.shard], { pos: center.join(',') });
    }

    onChangeDisplayType(type) {
        this.mapRef.toggleUsers(false);
        this.mapRef.toggleMinerals(false);

        switch (type) {
            case DISPLAY_NONE:
                break;

            case DISPLAY_OWNER: this.mapRef.toggleUsers(true);
                break;

            case DISPLAY_MINERALS: this.mapRef.toggleMinerals(true);
                break;

            default:
                break;
        }
    }

    onChangeIntervalType(interval: string) {
        const option = INTERVAL_OPTIONS.find(({ value }) => value === interval);

        this._intervalSbj.next(option);
    }

    onRoomOver(detail) {
        if (!detail) {
            return;
        }

        const { room } = detail;

        this.tooltipRef.setData({ roomName: room, ...statsCache[room] });
    }

    onRoomClick({
        button = LEFT_MOUSE_BUTTON,

        detail: {
            room,
            shard = this.settingsForm.value.shard
        }
    }) {
        const hash = this._routeSegmentService.getSegmentUrl('top.game-room', { room, shard });

        if (button === LEFT_MOUSE_BUTTON) {
            location.hash = `!${ hash }`;
            return;
        }

        if (button === WHEEL_MOUSE_BUTTON) {
            window.open(`#!${ hash }`, '_blank');
            return;
        }

        if (button === RIGHT_MOUSE_BUTTON) {
        }
    }

    async onSearch(room) {
        const shard = this.settingsForm.value.shard;

        let pos;
        try {
            if (/^(W|E)\d+(N|S)\d+$/i.test(room) === false) {
                const { user } = await this._apiService.get('user/find', { username: room });
                const data = await this._apiService.get('user/rooms', { id: user._id });

                const shards = data.shards || {
                    [WORLD_DEFAULT_SHARD]: data.rooms
                };

                const rooms = shards[shard];
                room = rooms[random(rooms.length)];
            }

            pos = roomToPos(room);
        } catch (err) {
            this._searchFailSbj.next(true);
            return;
        }

        this._updateRouteData([shard], { pos: pos.join(',') });
    }

    showWorldPreferences() {
        const shard = this.settingsForm.value.shard;

        this._dlgWorldPreferencesSrv.show({
            change: (preferences) => {
                this._updateRouteData([shard], preferences);
            }
        });
    }

    switchNoviceArea() {
        this._noviceAreaSbj.next();
    }

    userRespawn(): unknown {
        return this._apiService.post('user/respawn');
    }

    async randomNoviceRoom() {
        const shard = this.settingsForm.value.shard;

        try {
            const detail = await this._worldMapService.getRandomNoviceRoom(shard);

            this.onRoomClick({ detail });
        } catch (err) {
            // TODO: Need to add handler for random novice room error.
            // reject();
        }
    }

    // tslint:disable-next-line: member-ordering
    _queryParams = {};
    _updateRouteData(data, queryParams) {
        this._queryParams = {
            ...this._route.snapshot.queryParams,
            ...this._queryParams,
            ...queryParams
        };

        setTimeout(() => {
            this._router.navigate(['..', ...data], {
                queryParams: this._queryParams,
                relativeTo: this._route,
                replaceUrl: true
            });
        });
    }
}

function roomToPos(room: string): [number, number] {
    const point = Screeps.getPointByRoomName(room);

    return Object.values(point).map((v: number) => v + .5) as [number, number];
}

function random(min, max = 0) {
    if (!max) {
        [min, max] = [max, min];
    }

    return Math.floor(Math.random() * (max - min) + min);
}

function circleBresenhams(cx: number, cy: number, r: number): [number, number][] {
    const points = [];

    let x = 0;
    let y = r;

    let d = 3 - 2 * r;

    points.push(..._bresenhamsPoints(cx, cy, x, y));

    while (y >= x) {
        if (d > 0) {
            y--;
            d = d + 4 * (x - y) + 10;
        } else {
            d = d + 4 * x + 6;
        }

        x++;

        points.push(..._bresenhamsPoints(cx, cy, x, y));
    }

    return points;
}

function _bresenhamsPoints(cx: number, cy: number, x: number, y: number): [number, number][] {
    return [
        [cx + x, cy + y],
        [cx + x, cy + y - 1],
        [cx - x, cy + y],
        [cx - x, cy + y - 1],
        [cx + x, cy - y],
        [cx + x, cy - y + 1],
        [cx - x, cy - y],
        [cx - x, cy - y + 1],
        [cx + y, cy + x],
        [cx + y - 1, cy + x],
        [cx - y, cy + x],
        [cx - y + 1, cy + x],
        [cx + y, cy - x],
        [cx + y - 1, cy - x],
        [cx - y, cy - x],
        [cx - y + 1, cy - x]
    ];
}

const SCALE_RANGE = Screeps.MAX_SCALE - Screeps.MIN_SCALE;
function screepsScaleToPercent(scale) {
    return (scale - Screeps.MIN_SCALE) / SCALE_RANGE * 100;
}

function percentToScreepsScale(percent) {
    return SCALE_RANGE * (percent / 100) + Screeps.MIN_SCALE;
}
